import { createContext, type ReactNode, useCallback, useContext } from 'react';
import type {
  DataTableRowEventHandler,
  DataTableRowEventParams,
  SearchValues,
  TableData
} from '@/components/custom/erp';
import { refreshTableData } from '@/hooks/queries/refresh-table-data';
import type { GiaMua, GiaMuaInput } from '@/types/schemas';
import { useGiaMuaImport, useSearchTable } from './hooks';
import { getDataTableColumns } from './cols-definition';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

interface GiaMuaContextValue {
  // CRUD operations
  addItem: (item: GiaMuaInput) => Promise<GiaMua>;
  updateItem: (id: string, item: GiaMuaInput) => Promise<GiaMua>;
  deleteItem: (id: string) => Promise<void>;
  refreshData: () => Promise<void>;
  isLoading: boolean;
  data: GiaMua[];
  currentPage: number;
  totalItems: number;
  handlePageChange: (page: number) => void;

  // Search state
  searchState: {
    filterParams: SearchValues;
    searchResults: GiaMua[];
    totalCount: number;
    currentPage: number;
    isSearching: boolean;
  };
  handleSearchChange: (values: SearchValues) => void;
  hasFilterParams: boolean;
  tables: TableData[];

  // Form state
  showForm: boolean;
  showDelete: boolean;
  selectedObj: GiaMua | null;
  selectedRowIndex: string | null;
  formMode: 'add' | 'edit' | 'view';
  isCopyMode: boolean;
  handleCloseForm: () => void;
  handleCloseDelete: () => void;
  handleAddClick: () => void;
  handleEditClick: () => Promise<void>;
  handleViewClick: (row?: GiaMua) => Promise<void>;
  handleDeleteClick: () => void;
  handleCopyClick: () => Promise<void>;
  handleRowClick: DataTableRowEventHandler<GiaMua>;
  handleRowDoubleClick: DataTableRowEventHandler<GiaMua>;
  clearSelection: () => void;

  // Import state
  isImportDialogOpen: boolean;
  openImportDialog: () => void;
  closeImportDialog: () => void;
  isLoadingImport: boolean;
  setIsLoadingImport: (isLoading: boolean) => void;
}

const GiaMuaContext = createContext<GiaMuaContextValue | null>(null);

export function useGiaMuaContext() {
  const context = useContext(GiaMuaContext);
  if (!context) {
    throw new Error('useGiaMuaContext must be used within GiaMuaProvider');
  }
  return context;
}

const PAGE_SIZE = 20;

export function GiaMuaProvider({ children }: { children: ReactNode }) {
  const {
    data: purchasePrices,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    isLoading,
    searchData
  } = useCRUD<GiaMua, GiaMuaInput>({
    endpoint: QUERY_KEYS.GIA_MUA,
    pageSize: PAGE_SIZE
  });

  const { isImportDialogOpen, isLoadingImport, openImportDialog, closeImportDialog, setIsLoadingImport } =
    useGiaMuaImport();

  const {
    showForm,
    showDelete,
    formMode,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<GiaMua>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams, handleRefreshClick } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const handleRefreshTableData = useCallback(
    async () =>
      refreshTableData({
        hasFilterParams,
        refreshFilteredData: handleRefreshClick,
        refreshListData: refreshData
      }),
    [handleRefreshClick, hasFilterParams, refreshData]
  );

  const handleRowDoubleClick = async (params: DataTableRowEventParams<GiaMua>) => {
    await handleViewClick(params.row);
  };

  const displayData = hasFilterParams ? searchState.searchResults : purchasePrices || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = getDataTableColumns(handleViewClick);

  const tables: TableData[] = [
    {
      name: '',
      rows: displayData,
      columns: columns
    }
  ];

  return (
    <GiaMuaContext.Provider
      value={{
        // CRUD
        addItem,
        updateItem,
        deleteItem,
        refreshData: handleRefreshTableData,
        isLoading,
        data: displayData,
        currentPage: resolvedCurrentPage,
        totalItems: resolvedTotalItems,
        handlePageChange: onPageChange,

        // Search
        searchState,
        handleSearchChange,
        hasFilterParams,
        tables,

        // Form state
        showForm,
        showDelete,
        selectedObj: detail,
        selectedRowIndex,
        formMode,
        isCopyMode,
        handleCloseForm,
        handleCloseDelete,
        handleAddClick,
        handleEditClick,
        handleViewClick,
        handleDeleteClick,
        handleCopyClick,
        handleRowClick,
        handleRowDoubleClick,
        clearSelection,

        // Import state
        isImportDialogOpen,
        openImportDialog,
        closeImportDialog,
        isLoadingImport,
        setIsLoadingImport
      }}
    >
      {children}
    </GiaMuaContext.Provider>
  );
}
